/**
 * «Уведомить» по записи «Бездействие»: предпросмотр статусов дня до и после «Ушел», минуты по статусам,
 * кому и текст сообщения → «Сменить статусы и отправить». Право `activity_idle_notify`.
 * Спека `docs/specs/2026-11-04-idle-notify-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { type IdleStatusRow, loadIdleNotify, sendIdleNotify } from "@/lib/activity";

const hms = (iso: string | null) => (iso ? iso.slice(11, 16) : "…");

function minutes(m: number): string {
  const h = Math.floor(m / 60);
  const r = Math.round(m - h * 60);
  return h ? `${h} ч ${r} мин` : `${r} мин`;
}

function Rows({ rows, title }: { rows: IdleStatusRow[]; title: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="text-[12px] font-medium text-fg-muted">{title}</div>
      <ul className="flex flex-col gap-0.5 tabular-nums">
        {rows.map((r, n) => (
          <li
            key={`${r.id ?? "n"}-${n}`}
            className={r.new ? "rounded-[var(--radius-ui)] bg-warn-bg px-1.5 text-fg" : "px-1.5 text-fg-muted"}
          >
            {hms(r.from)}–{hms(r.to)} {r.name}
            {r.new && <span className="text-fg-faint"> · добавим</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function IdleNotifyDialog({ id, onClose: close_ }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["idle-notify", id], queryFn: () => loadIdleNotify(id), staleTime: 0 });
  const [text, setText] = useState<string | null>(null);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  const p = q.data;
  // Список обновляем при закрытии, а не сразу: закрытая запись уходит из «неразобранных», карточка
  // размонтируется вместе с окном, и итог «статусы сменены, сообщение отправлено» никто не увидит.
  const send = useMutation({
    mutationFn: () => sendIdleNotify(id, (text ?? p?.text ?? "").trim(), p?.fingerprint ?? ""),
  });
  const onClose = () => {
    if (send.isSuccess) void qc.invalidateQueries({ queryKey: ["activity-control"] });
    close_();
  };
  const body = text ?? p?.text ?? "";
  const telegramBad = !!p?.to && !p.telegram_ok;
  const blocked = p?.blocked ?? (telegramBad ? p?.telegram_error ?? "Telegram недоступен" : null);
  const done = send.data?.notify;
  const btn =
    "h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[12.5px] text-fg hover:bg-bg-hover disabled:opacity-40";

  return createPortal(
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/30 p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), onClose())}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Уведомить о бездействии"
        className="flex max-h-[92vh] w-full max-w-3xl flex-col gap-3 overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg p-4 text-[12.5px] text-fg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-[14px] font-medium">
            Уведомить о бездействии{p?.name ? ` · ${p.name}` : ""}
            {p?.day ? `, ${p.day.slice(8, 10)}.${p.day.slice(5, 7)}` : ""}
          </div>
          <button
            ref={close}
            type="button"
            className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg"
            aria-label="Закрыть"
            title="Закрыть (Esc)"
            onClick={onClose}
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        {q.isPending && <div className="text-fg-muted">Смотрю статусы дня в CRM…</div>}
        {q.isError && <div className="text-err">Не удалось загрузить: {String(q.error.message)}</div>}

        {p?.before && p.after && (
          <>
            <div className="flex flex-col gap-1">
              {p.segments?.map((s) => (
                <div key={s.from} className={s.ok ? "text-fg" : "text-fg-faint"}>
                  {s.from}–{s.to}: {s.ok ? "поставим «Ушел», после — прежний статус" : `не меняем — ${s.reason}`}
                </div>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Rows rows={p.before} title="Сейчас в CRM" />
              <Rows rows={p.after} title="Станет" />
            </div>
            <div>
              Время по статусам: {minutes(p.minutes_before ?? 0)} → <b>{minutes(p.minutes_after ?? 0)}</b>{" "}
              <span className="text-fg-muted">(−{minutes((p.minutes_before ?? 0) - (p.minutes_after ?? 0))})</span>.{" "}
              <span className="text-fg-faint">Акцепт и зарплату это не меняет.</span>
            </div>
            <div className="text-fg-muted">
              Кому:{" "}
              {p.to ? (
                <span className="text-fg">
                  {p.name} · {p.to.login ? `@${p.to.login}` : `id ${p.to.tg_id}`}
                </span>
              ) : (
                <span className="text-warn">Telegram не найден — сменим только статусы</span>
              )}{" "}
              · с вашего личного Telegram
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-[12px] text-fg-muted">Сообщение</span>
              <textarea
                value={body}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                disabled={!!done}
                className="rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5 text-[12.5px] text-fg outline-none focus:border-accent"
              />
            </label>
          </>
        )}

        {blocked && !done && (
          <div className="text-err" role="alert">
            {blocked}
          </div>
        )}
        {send.isError && (
          <div className="text-err" role="alert">
            Не получилось: {String(send.error.message)}
          </div>
        )}
        {done && (
          <div className={done.status === "sent" ? "text-ok" : "text-warn"} role="status">
            Статусы сменены (−{minutes(done.minutes)}).{" "}
            {done.status === "sent" ? "Сообщение отправлено." : `Сообщение не ушло: ${done.error ?? "—"}. Отправить ещё раз можно из карточки.`}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {!done && (
            <button
              type="button"
              className={`${btn} border-accent`}
              disabled={!p?.fingerprint || !!blocked || send.isPending || !body.trim()}
              onClick={() => send.mutate()}
            >
              {send.isPending ? "Меняю статусы и отправляю…" : p?.to ? "Сменить статусы и отправить" : "Сменить статусы"}
            </button>
          )}
          <button type="button" className={btn} onClick={onClose}>
            {done ? "Готово" : "Отмена"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
